// KBDescriptionPopup.tsx (or paste into same file above return)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DraggableCore, type DraggableData, type DraggableEvent } from 'react-draggable';
import { ChangeEventArgs, HtmlEditor, Inject, RichTextEditorComponent, Toolbar } from '@syncfusion/ej2-react-richtexteditor';
import { PositionDataModel } from '@syncfusion/ej2-react-popups';
import KnowledgeBookActions from '../actions/KnowledgeBookActions';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
import FacilityActions from '../actions/FacilityActions';
import ViewerActions from '@facility/actions/ViewerActions';
import UrlUtilities from '@shared/common/UrlUtilities';
import ComponentHelpers from '@facility/helpers/ComponentHelpers';
import ViewerStore from '@facility/stores/ViewerStore';
import FacilityStore from '@facility/stores/FacilityStore';

interface KBDescriptionPopupProps {
    kb?: IKnowledgeBook | null;
    kv?: IKnowledgeViewDetail | null
    dialogPosition: PositionDataModel;
    onClose: () => void;         // invoked when user clicks X
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

const KBDescriptionPopup: React.FC<KBDescriptionPopupProps> = ({ kb, kv, dialogPosition, onClose }) => {
    const mountedRef = useRef(false);
    const rteRef = useRef(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const [manualPosition, setManualPosition] = useState(KnowledgeBookStore.getKBPopupPosition);
    const [isKBPopupPinned, setIsKBPopupPinned] = useState<boolean>(
        KnowledgeBookStore.getKBPopupPinnedStatus()
    );
    const [favoriteKnowledgeBookIds, setFavoriteKnowledgeBookIds] = useState<string[]>(
        KnowledgeBookStore.getFavoriteKnowledgeBookIds()
    );
    const [kbViewingMode, setKbViewingMode] = useState<KnowledgeBookViewingMode>(KnowledgeBookStore.getKnowledgeBookViewingMode());
    const [knowledgeBookViews, setKnowledgeBookViews] = useState<IKnowledgeView[]>(
        KnowledgeBookStore.getAllKnowledgeView()
    );
    const [facilityAssetSetsBarOpen, setFacilityAssetSetsBarOpen] = useState(
        FacilityStore.getFacilityAssetSetsBarOpen()
    );
    const canModifyKnowledgeView = ComponentHelpers.hasModifyKnowledgeBookPermission(kb);
    const [expandedKvDescription, setExpandedKvDescription] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [editState, setEditState] = useState(() => ({
        id: ViewerStore.getKnowledgeViewEditID(), busy: ViewerStore.isKnowledgeViewEditBusy(),
        dirty: ViewerStore.isKnowledgeViewDirty(),
    }));
    const editing = !!editState.id && editState.id === kv?.ID;
    useEffect(() => {
        if (editing) setExpandedKvDescription(false);
    }, [editing, editState.id]);

    const getDescriptionToggleTitle = (isExpanded: boolean) => {
        const translationKey = isExpanded
            ? "facility:knowledgeBook.hideKnowledgeBookDescription"
            : "facility:knowledgeBook.showKnowledgeBookDescription";
        return InovxLoader.t(translationKey);
    }

    // Keep expanded true while mouse over; collapse when leaves
    const handleEnter = () => setExpanded(true);
    const handleLeave = () => setExpanded(false);

    const onChange = useCallback(() => {
        if (!mountedRef.current) return;
        setEditState({ id: ViewerStore.getKnowledgeViewEditID(), busy: ViewerStore.isKnowledgeViewEditBusy(), dirty: ViewerStore.isKnowledgeViewDirty() });
        setKbViewingMode(KnowledgeBookStore.getKnowledgeBookViewingMode())
        setIsKBPopupPinned(KnowledgeBookStore.getKBPopupPinnedStatus());
        setManualPosition(KnowledgeBookStore.getKBPopupPosition());
        setKnowledgeBookViews(KnowledgeBookStore.getAllKnowledgeView());
        setFavoriteKnowledgeBookIds(KnowledgeBookStore.getFavoriteKnowledgeBookIds());
        setFacilityAssetSetsBarOpen(FacilityStore.getFacilityAssetSetsBarOpen());
    }, []);
    useEffect(() => {
        mountedRef.current = true;

        KnowledgeBookStore.addChangeListener(onChange);
        FacilityStore.addChangeListener(onChange);
        ViewerStore.addChangeListener(onChange);

        return () => {
            mountedRef.current = false;
            KnowledgeBookStore.removeChangeListener(onChange);
            FacilityStore.removeChangeListener(onChange);
            ViewerStore.removeChangeListener(onChange);
        };
    }, [onChange]);


    useEffect(() => {
        KnowledgeBookActions.getFavoriteKnowledgeBookIds();
    }, [kb?.ID]);

    const pinUnpinKBPopup = () => {
        if (!isKBPopupPinned) FacilityActions.closeKBbar();
        KnowledgeBookActions.pinUnpinKBPopup(!isKBPopupPinned)
    }

    const dragPopup = (_event: DraggableEvent, data: DraggableData) => {
        if (!KnowledgeBookStore.getKBPopupPinnedStatus() || (!data.deltaX && !data.deltaY)) return;
        const popup = popupRef.current;
        // Use the same targets as the Mini Map in the active viewer.
        const viewer = document.querySelector<HTMLElement>('#viewer-3d, #pano-viewer');
        if (!popup || !viewer) return;
        const popupBounds = popup.getBoundingClientRect();
        const viewerBounds = viewer.getBoundingClientRect();
        const left = Math.max(viewerBounds.left, Math.min(popupBounds.left + data.deltaX, viewerBounds.right - popupBounds.width));
        const top = Math.max(viewerBounds.top, Math.min(popupBounds.top + data.deltaY, viewerBounds.bottom - popupBounds.height));
        const position = {
            X: parseFloat(popup.style.left) + left - popupBounds.left,
            Y: parseFloat(popup.style.top) + top - popupBounds.top,
        };
        KnowledgeBookStore.setKBPopupPosition(position);
        setManualPosition(position);
    };

    const toggleFavoriteKnowledgeBook = async () => {
        await KnowledgeBookActions.toggleFavoriteKnowledgeBook(kb);
    };

    const isFavorite = favoriteKnowledgeBookIds.indexOf(kb?.ID) >= 0;

    const currentKVIndex = React.useMemo(() => {
        if (!kv) return -1;
        return knowledgeBookViews.findIndex(
            (item) => item.ID === kv.ID
        );
    }, [knowledgeBookViews, kv]);


    const hasPrevious = currentKVIndex > 0;
    const hasNext = currentKVIndex !== -1 && currentKVIndex < knowledgeBookViews.length - 1;

    const openPreviousView = async () => {
        if (!hasPrevious) return;
        const prevKV = knowledgeBookViews[currentKVIndex - 1];
        await FacilityActions.openKnowledgeViewInViewer(prevKV);
    };

    const openView = async () => {
        const currentKV = knowledgeBookViews[0];
        await FacilityActions.openKnowledgeViewInViewer(currentKV);
    };

    const openNextView = async () => {
        if (!hasNext) return;
        const nextKV = knowledgeBookViews[currentKVIndex + 1];
        await FacilityActions.openKnowledgeViewInViewer(nextKV);
    };

    const openKnowledgeViewForm = React.useCallback(() => {
        const selectedKnowledgeView = ViewerStore.getSelectedKnowledgeView();
        if (selectedKnowledgeView) {
            KnowledgeBookActions.beginEditKnowledgeView(selectedKnowledgeView);
        }
    }, []);

    const KnowledgeViewDescription = (description: string) => {
        if (!description) return;
        return (<div className={`kv-description ${expanded || isKBPopupPinned ? 'kb-expanded' : ''}`}>
            <RichTextEditorComponent
                ref={rteRef}
                readonly={true}
                value={description}
                cssClass="kb-rte-transparent editor-min-height">
                <Inject services={[HtmlEditor]} />
            </RichTextEditorComponent>
        </div>)
    }
    if (!kb) return null;
    const showEditControls = !!kv && kbViewingMode === 'manageKV' && !facilityAssetSetsBarOpen && canModifyKnowledgeView && (expanded || isKBPopupPinned);
    const position = isKBPopupPinned && manualPosition ? manualPosition : dialogPosition;
    return (
        <DraggableCore nodeRef={popupRef} disabled={!isKBPopupPinned} handle=".kb-desc-header" cancel="button"
            onDrag={dragPopup} onStop={dragPopup}>
        <div
            ref={popupRef}
            className={`kb-desc-popup-wrapper${showEditControls ? ' kb-has-edit-controls' : ''}`}
            style={{ left: position?.X ?? 67, top: position?.Y ?? 62 }}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            aria-live="polite"
            aria-busy={editState.busy}
        >
            <div className={`hover-box ${expanded || isKBPopupPinned ? 'kb-expanded' : ''}`}>
                <div className="kb-desc-header" style={{ cursor: isKBPopupPinned ? 'move' : undefined }}>
                    <div className="kb-desc-title" title={kb.Name}>{kb.Name}</div>
                    <div className="kb-action">
                        <button
                            className={`kb-btn kb-btn-favorite${isFavorite ? ' active' : ''}`}
                            title={isFavorite ? InovxLoader.t("facility:knowledgeBook.removeFavorite") : InovxLoader.t("facility:knowledgeBook.addFavorite")}
                            onClick={() => toggleFavoriteKnowledgeBook()}>
                            <span className={`fa ${isFavorite ? 'fa-star' : 'fa-star-o'}`} aria-hidden="true" />
                        </button>
                        <button className={`kb-btn`} title={isKBPopupPinned ? InovxLoader.t("facility:knowledgeBook.unpinKbPopup") : InovxLoader.t("facility:knowledgeBook.pinKbPopup")} onClick={() => pinUnpinKBPopup()} >
                            <span className={`kb-action-icon ${isKBPopupPinned ? 'btn-unpin' : 'btn-pin'}`}></span>
                        </button>
                        <button className="kb-desc-close" title={InovxLoader.t("facility:knowledgeBook.closeKbDescription")} disabled={editState.busy} onClick={onClose} />
                    </div>
                </div>
                <div className={`${(kb.Description || (kbViewingMode === 'manageKV' && !facilityAssetSetsBarOpen)) ? "box-description" : ""}`}>
                    {kb.Description && <div className="kb-description">
                        {kb.Description}
                    </div>}
                    {(kbViewingMode === 'manageKV' && !facilityAssetSetsBarOpen) && <>{(kv) ?
                        <div className="d-flex j-csb a-ic mt-5px">
                            <div className="kv-details-row">
                                <div className="kv-title" title={kv.Name}>{kv.Name}</div>
                                {canModifyKnowledgeView && <button
                                    className="kb-btn kv-edit-button"
                                    title={InovxLoader.t('facility:action.editView')}
                                    onClick={() => openKnowledgeViewForm()}>
                                        <img src={`${UrlUtilities.getBaseApplicationUrl()}images/KB/Pen.png`} alt={InovxLoader.t('facility:action.editView')} />
                                    {/* <i className="fa fa-pencil" aria-hidden="true"></i> */}
                                </button>}
                            </div>
                            <div className="kv-navigation">
                                <button
                                    className={`kv-btn btn-prev${!hasPrevious ? '-disabled' : ''}`}
                                    title={InovxLoader.t("facility:knowledgeBook.openPreviousView")}
                                    disabled={editState.busy || !hasPrevious}
                                    onClick={openPreviousView}
                                />
                                <button
                                    className={`kv-btn ml-10 btn-next${!hasNext ? '-disabled' : ''}`}
                                    title={InovxLoader.t("facility:knowledgeBook.openNextView")}
                                    disabled={editState.busy || !hasNext}
                                    onClick={openNextView}
                                />
                            </div>
                        </div> : knowledgeBookViews.length > 0 && !facilityAssetSetsBarOpen && <div className="d-flex j-csb a-ic mt-5px">
                            <div className="kv-title muted" title={knowledgeBookViews[0].Name}>{knowledgeBookViews[0].Name}</div>
                            <div className="d-flex j-csb">
                                <button
                                    className="kv-btn btn-open"
                                    title={InovxLoader.t("facility:knowledgeBook.openView")}
                                    onClick={openView}
                                />
                            </div>
                        </div>}
                    </>}
                    
                </div>
                {((kbViewingMode === 'manageKV' && !facilityAssetSetsBarOpen) && kv && kv.Description && (expanded || isKBPopupPinned)) && <button
                    className={`no-btn btn-bottom`}
                    title={getDescriptionToggleTitle(expandedKvDescription)}
                    onClick={() => setExpandedKvDescription(!expandedKvDescription)}
                >
                    <i className={`btn-icon ${expandedKvDescription ? 'up-arrow' : 'down-arrow'}`} />
                </button>}
            </div>
            {((kbViewingMode === 'manageKV' && !facilityAssetSetsBarOpen) && kv?.Description && expandedKvDescription) && KnowledgeViewDescription(kv.Description)}
            {showEditControls && <div className="kb-view-edit-actions">
                {editing ? <>
                    <button type="button" className="btn btn-default" disabled={editState.busy}
                        onClick={() => ViewerActions.checkForKnowledgeViewChanges()}>{InovxLoader.t('facility:preferences.cancelButton')}</button>
                    <button type="button" className="btn btn-primary" disabled={editState.busy || !editState.dirty}
                        onClick={() => void ViewerActions.saveKnowledgeViewEditSession()}>{InovxLoader.t('facility:dataSources.save')}</button>
                </> : <button type="button" className="btn btn-primary knowledge-views-edit-button" disabled={editState.busy}
                    onClick={() => ViewerActions.beginKnowledgeViewEditSession()}>{InovxLoader.t('facility:action.editView')}</button>}
            </div>}
        </div>
        </DraggableCore>
    );
};

export default KBDescriptionPopup;
